<template>
  <div v-if="blok.title" class="content-feedback">
    <div class="content-feedback__content">
      <h3 class="content-feedback__title">
        {{ blok.title }}
        <SbIcon name="heart-feedback" class="content-feedback__icon" />
      </h3>
      <p class="content-feedback__subtitle">{{ blok.subtitle }}</p>
      <div class="content-feedback__rating">
        <SbIconButton
          v-for="n in 5"
          :key="n"
          :label="$t('Rate {n} stars', { n })"
          size="small"
          icon="rating-star"
          class="content-feedback__rating-star"
          :class="{
            'content-feedback__rating-star--active': n <= selectedRating,
          }"
          no-height
          @mouseover="hoverRating(n)"
          @focus="hoverRating(n)"
          @click="() => saveRating(n)"
          @keyup.enter="saveRating(n)"
          @mouseleave="blurRating"
          @blur="blurRating"
        />
        <Transition name="bounce">
          <span v-if="showFeedback" class="content-feedback__rating-feedback">
            {{
              $t('Thanks {userName} for your feedback!', {
                userName: user.firstname,
              }).replace(/\s+/g, ' ')
            }}
          </span>
        </Transition>
      </div>
    </div>
    <BaseStoryblokRichText
      v-if="hasFooterText"
      class="content-feedback__footer"
      :doc="blok.footer_text"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { type ISbRichtext } from 'storyblok-js-client'
import { useApps } from '@/store/modules/App/apps'
import { useTracking } from '@/store/modules/App/tracking'
import { useUser } from '@/store/modules/App/user'
import { SbIcon, SbIconButton } from '@storyblok/design-system'
import BaseStoryblokRichText from '@/components/Base/BaseStoryblokRichText/BaseStoryblokRichText.vue'

const props = defineProps<{
  blok: {
    title: string
    subtitle: string
    footer_text: ISbRichtext
  }
  title: string
  id: string // is the backend_app_slug
}>()

const { user } = useUser()
const { analyticsTrack } = useTracking()
const { loadAppRating, saveAppRating } = useApps()

const selectedRating = ref(0)
const userRating = ref(0)
const showFeedback = ref(false)

const hasFooterText = computed(
  (): boolean =>
    !!props.blok.footer_text?.content?.[0]?.content &&
    props.blok.footer_text.content[0].content.length > 0,
)

const hoverRating = (rating: number): void => {
  selectedRating.value = rating
}

const blurRating = (): void => {
  if (!userRating.value) {
    selectedRating.value = 0
  }
}

const saveRating = async (rating: number): Promise<void> => {
  try {
    await saveAppRating({
      rating,
      app_slug: props.id,
    })
  } catch (error) {
    console.error('Error saving feature rating', error)
  }

  userRating.value = rating
  showFeedback.value = true
  setTimeout(() => {
    showFeedback.value = false
  }, 2000)
  if (props.id) {
    analyticsTrack({
      event: 'click_labs_detail_page_feedback',
      payload: {
        feature_name: props.title,
        context: 'labs',
      },
    })
  } else {
    console.warn('No tracking id provided for feedback')
  }
}

onMounted(async () => {
  try {
    const ratings = await loadAppRating()
    // load the current rating for the app per user, if any

    userRating.value =
      ratings.app_ratings?.find((rating) => rating.app_slug === props.id)
        ?.rating || 0

    selectedRating.value = userRating.value
  } catch (error) {
    console.error('Error loading app rating', error)
  }
})
</script>

<style lang="scss">
@import './content-feedback';
</style>
